export function ensureArray<T>(item: T | Array<T> | null | undefined): Array<T>;
export function ensureArray<T>(item: T | ReadonlyArray<T> | null | undefined): ReadonlyArray<T>;
export function ensureArray<T>(item: T | ReadonlyArray<T> | null | undefined): ReadonlyArray<T> {
	if (Array.isArray(item)) return item;
	if (item) return [item as T];
	else return [];
}

/**
 * Takes an array of objects and sorts them according to a property name's match against a search string.
 * @param arr the array to be sorted - this is assumed to be an array of of objects.
 * @param inputStr the search string to compare for sorting.
 * @param property the property name to compare.
 * @returns a sorted array of objects based on the passed in property and its alphabetical relationship to the search string.
 */
export const sortArrayByPropertyMatch = (arr: Array<any>, inputStr: string, property: string) => {
	return arr.sort((a, b) => {
		// get everything to lowercase
		const lowerA = a[property].toLowerCase();
		const lowerB = b[property].toLowerCase();
		const lowerInput = inputStr.toLowerCase();

		// check if either starts with the user input
		const aStartsWith = lowerA?.startsWith(lowerInput);
		const bStartsWith = lowerB?.startsWith(lowerInput);

		// return the better match
		if (aStartsWith && !bStartsWith) return -1;
		if (!aStartsWith && bStartsWith) return 1;

		// check if the input string is found anywhere else in the string
		const aIncludes = lowerA?.includes(lowerInput);
		const bIncludes = lowerB?.includes(lowerInput);

		if (aIncludes && !bIncludes) return -1;
		if (!aIncludes && bIncludes) return 1;

		return a.name?.localeCompare(b?.name ?? '');
	});
};
/**
 * Simply checks if an array's length is greater than 0.
 * @param arr the array whose length is to be checked. It must be an array.
 * @returns a boolean indicating whether the array length is greater than 0.
 */
export function lengthGreaterThanZero(arr: any[]) {
	return arr.length > 0;
}

/**
 * Returns the value itself if it's not an array, or the first element if it is an array.
 * If the input is null or undefined, returns undefined.
 * @param item The value or array of values.
 * @returns The value itself, or the first element if an array, or undefined.
 */
export function ensureSingle<T>(item: T | Array<T> | null | undefined): T | undefined {
	if (Array.isArray(item)) return item[0];
	return item ?? undefined;
}
